import { CheckOutlined, Close } from '@mui/icons-material';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent, { DialogContentProps } from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import React from 'react';

import Button from '../Buttons/Button';

interface CustomDialogProps extends DialogProps {
  onConfirm?: () => void;
  onCancel: () => void;
  dialogContentProps?: DialogContentProps;
  confirmButtonText?: string;
  cancelButtonText?: string;
  customContent?: JSX.Element;
  headerAction?: JSX.Element;
  /** Secondary text under the title or `headerContent`. */
  subtitle?: React.ReactNode;
  /**
   * Rich header content rendered on the left in place of the plain `title`
   * Typography. Use when the header needs its own layout (indicators, chips).
   */
  headerContent?: JSX.Element;
  /** Action controls rendered on the right of the header, before the close button. */
  headerActions?: JSX.Element;
  /** Render a close (x) IconButton on the right of the header. */
  showCloseButton?: boolean;
  /** Skip the footer actions row entirely (e.g. when closing via the header x). */
  hideActions?: boolean;
  /**
   * Custom footer content rendered in place of the default cancel/confirm
   * buttons. Pass `null` to render no footer at all (e.g. an action-less tab);
   * leave undefined to keep the default footer.
   */
  footerContent?: JSX.Element | null;
  /** Pin the dialog to a tall fixed height so its content area doesn't resize. */
  fullHeight?: boolean;
  confirmIcon?: JSX.Element;
  cancelStartIcon?: JSX.Element;
  cancelEndIcon?: JSX.Element;
  confirmDisabled?: boolean;
  cancelDisabled?: boolean;
}

const GenericDialog: React.FC<CustomDialogProps> = ({
  open,
  title,
  dialogContentProps,
  onConfirm,
  onCancel,
  onClose,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  customContent,
  maxWidth,
  headerAction,
  subtitle,
  headerContent,
  headerActions,
  showCloseButton,
  hideActions,
  footerContent,
  fullHeight,
  confirmIcon,
  cancelStartIcon,
  cancelEndIcon,
  confirmDisabled,
  cancelDisabled,
  ...props
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose ?? onCancel}
      maxWidth={maxWidth}
      fullWidth={props.fullWidth}
      PaperProps={fullHeight ? { sx: { height: '90vh' } } : undefined}
    >
      {(title ||
        headerContent ||
        headerAction ||
        headerActions ||
        showCloseButton) && (
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              paddingBottom: 0,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              {headerContent ?? <Typography variant='h4'>{title}</Typography>}
              {subtitle && (
                <Typography
                  component='span'
                  variant='body2'
                  color='text.secondary'
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
            {(headerAction || headerActions || showCloseButton) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {headerAction}
                {headerActions}
                {(headerAction || headerActions) && showCloseButton && (
                  <Divider orientation='vertical' flexItem sx={{ mx: 0.5 }} />
                )}
                {showCloseButton && (
                  <IconButton
                    aria-label='Close'
                    onClick={(event) =>
                      onClose ? onClose(event, 'escapeKeyDown') : onCancel()
                    }
                  >
                    <Close />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>
        </DialogTitle>
      )}

      <DialogContent dividers {...dialogContentProps}>
        {customContent}
      </DialogContent>
      {!hideActions && footerContent === undefined && (
        <DialogActions
          sx={{
            mt: 1.5,
            justifyContent: 'flex-end',
          }}
        >
          <Button
            onClick={onCancel}
            color='primary'
            autoFocus
            variant='text'
            startIcon={cancelStartIcon}
            endIcon={cancelEndIcon ?? <Close />}
            disabled={cancelDisabled}
          >
            {cancelButtonText}
          </Button>
          {onConfirm && (
            <Button
              onClick={onConfirm}
              color='primary'
              autoFocus
              variant='contained'
              endIcon={confirmIcon ?? <CheckOutlined />}
              disabled={confirmDisabled}
            >
              {confirmButtonText}
            </Button>
          )}
        </DialogActions>
      )}
      {!hideActions && footerContent != null && (
        <DialogActions sx={{ mt: 1.5, justifyContent: 'flex-end' }}>
          {footerContent}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default GenericDialog;
